/**
 * Dermabella — Motore free gift.
 *
 * Riconcilia il carrello con le offerte definite in #dermabella-offers:
 *  - aggiunge i regali mancanti quando il trigger è presente;
 *  - rimuove i regali orfani (trigger assente);
 *  - riporta al tetto le quantità di ciascun regalo;
 *  - rispetta il rifiuto volontario (attributo carrello _gift_declined_<id>);
 *  - sospende un'offerta quando un'altra offerta elencata nel suo
 *    `suppressed_by` è attiva (trigger presente) — vedi sotto.
 *
 * Ogni offerta ha uno o PIÙ regali (offer.gifts: [{variant_id, qty}, ...]).
 * A livello 3 flaconi (offerta `omaggi-3fl`) i due regali (Pochette + Mini
 * Siero) sono una COPPIA indivisibile: lo sconto automatico lato server che
 * li azzera è applicato a un SET e non si attiva con un solo elemento nel
 * set. Di conseguenza qui il rifiuto è sempre di offerta intera: declinare
 * anche un solo regalo azzera TUTTE le righe regalo dell'offerta, e
 * "Riaggiungi" le ripristina tutte insieme — mai una singola riga regalo
 * resta sola nel carrello a rischio di venire addebitata a prezzo pieno.
 * Per lo stesso motivo `pochette-2fl` (che condivide la Pochette con
 * `omaggi-3fl`) porta `suppressed_by: ['omaggi-3fl']`: in un carrello
 * ibrido (2 flaconi + 3 flaconi) il suo regalo viene sospeso e i regali
 * arrivano solo dall'offerta combinata, coerente con lo sconto server.
 *
 * Lo stato è SEMPRE riletto da /cart.js: nessuna fiducia in stato in memoria.
 * La protezione economica è degli sconti automatici lato server: qui si cura
 * solo l'esperienza. Se questo script fallisce, nessun regalo indebito.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE = 'dermabella-gift-engine';
const DECLINED_PREFIX = '_gift_declined_';

class DermabellaGiftEngine {
  /** Tutte le offerte free_gift configurate, di ogni braccio — non cambia dopo l'init. */
  #allOffers = [];
  /** Sottoinsieme di #allOffers per il braccio attivo — questo sì cambia, su rebound. */
  #offers = [];
  #busy = false;
  #pending = false;
  /** Opzione clearDeclined da applicare alla riconciliazione accodata mentre una è in corso. */
  #pendingClearDeclined = false;
  #ownDispatch = false;
  /** Chiave riga regalo -> offer id, dall'ultima riconciliazione (per rilevare i rifiuti). */
  #giftKeys = new Map();
  /** Offer id il cui regalo è appena stato rimosso volontariamente dall'utente. */
  #pendingDecline = new Set();
  /**
   * Offer id il cui sconto server non ha azzerato il regalo (sconto disattivato
   * o rinominato in admin): il motore smette di riaggiungerlo per la sessione,
   * per non generare churn add→pagamento→rimozione a ogni riconciliazione.
   */
  #disabledOffers = new Set();

  init() {
    // Nel theme editor il motore non deve mutare il carrello di anteprima.
    if (window.Shopify && Shopify.designMode) return;
    const el = document.getElementById('dermabella-offers');
    if (!el) return;
    try {
      const parsed = JSON.parse(el.textContent);
      this.#allOffers = (parsed.offers || []).filter((o) => o.type === 'free_gift');
    } catch (error) {
      console.warn('[gift-engine] configurazione non valida', error);
      return;
    }
    // Zero offerte free_gift configurate ovunque, per qualunque braccio: non c'è
    // nulla da riconciliare ora né dopo un eventuale rebound, il motore può
    // restare del tutto inerte. ATTENZIONE: non testare invece su #offers (il
    // sottoinsieme del braccio corrente) — se il braccio iniziale non ne avesse
    // (oggi il braccio B), i listener qui sotto non verrebbero mai agganciati, e
    // un rebound successivo verso un braccio CON offerte non avrebbe nessuno ad
    // ascoltarlo: il motore resterebbe inerte per l'intera sessione, non solo
    // disallineato.
    if (this.#allOffers.length === 0) return;

    this.#warnOnDanglingSuppressions();
    this.#refilterOffers();

    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (this.#ownDispatch) return; // anti-loop: evento generato da questo motore
      this.#detectDecline(event);
      const promise = event.promise || Promise.resolve();
      promise.catch(() => {}).finally(() => this.reconcile());
    });

    // Il carrello può vincere sul cookie dopo che questo motore ha già filtrato
    // le offerte sul braccio sbagliato (db-exp-assign.liquid, evento dispacciato
    // da bundle-selector.liquid): senza questo, #offers resterebbe congelato sul
    // braccio d'ingresso per tutta la sessione. Non ri-parsa #allOffers (già in
    // memoria, il JSON non cambia), solo rifiltra. reconcile() poi rimuove da
    // solo le righe regalo del braccio precedente: non sono più fra le offerte
    // correnti, quindi cadono nella pulizia "offerte non più esistenti" già
    // presente più sotto — nessuna logica di rimozione aggiuntiva necessaria qui.
    document.addEventListener('db-exp:rebound', () => {
      this.#refilterOffers();
      this.reconcile();
    });

    this.reconcile();
  }

  /**
   * Con più bracci nel DOM, il motore deve vedere solo le offerte del braccio
   * assegnato: altrimenti riconcilierebbe regali di offerte che il visitatore
   * non ha mai visto, e che nessuno sconto automatico coprirebbe. Il doppio `||`
   * mantiene il comportamento di sempre quando nessun esperimento è attivo:
   * offerte senza `arm`, o senza data-db-arm in pagina, passano tutte.
   */
  #refilterOffers() {
    const activeArm = document.documentElement.dataset.dbArm || null;
    this.#offers = this.#allOffers.filter(
      (o) => !o.arm || !activeArm || o.arm === activeArm
    );
  }

  #offersForCart(cart) {
    const activeIds = new Set(this.#offers.map((offer) => offer.id));
    const giftOfferIds = new Set(
      cart.items
        .filter((item) => this.#isGiftItem(item))
        .map((item) => item.properties._offer_id)
    );
    const triggerVariantIds = new Set(
      cart.items
        .filter((item) => !this.#isGiftItem(item))
        .map((item) => item.variant_id)
    );
    return this.#allOffers.filter(
      (offer) =>
        activeIds.has(offer.id) ||
        giftOfferIds.has(offer.id) ||
        offer.trigger_variant_ids.some((variantId) => triggerVariantIds.has(variantId))
    );
  }

  /**
   * suppressed_by è intra-braccio per costruzione (dermabella-offers-config.liquid
   * prefissa sempre col proprio arm_id sia l'id che dichiara sia quelli
   * sospesi — mai con quello di un bersaglio diverso): un offer_id scritto per
   * sbaglio nel registro — un refuso, o un tentativo di sospendere un'offerta di
   * un ALTRO braccio, che verrebbe comunque ri-prefissato sul braccio sbagliato —
   * non produce mai un riferimento incrociato valido verso un'offerta reale:
   * produce un riferimento che non corrisponde a NESSUNA offerta esistente. La
   * sospensione allora è un no-op silenzioso — il regalo che doveva essere
   * sospeso resta a carrello, la guardia lo addebita e lo rimuove più sotto,
   * senza log — proprio nel momento (compilare un braccio nuovo copiando
   * l'altro) in cui l'errore è più probabile. Gira una sola volta all'avvio, su
   * TUTTE le offerte (non il sottoinsieme filtrato per braccio: un difetto nel
   * braccio B deve comparire anche a un visitatore assegnato ad A), così appare
   * in console durante il collaudo — che già prevede di controllarla — invece
   * che scoprirlo da un carrello ibrido reale. Solo un avviso: non blocca né
   * modifica nulla.
   */
  #warnOnDanglingSuppressions() {
    const knownIds = new Set(this.#allOffers.map((offer) => offer.id));
    for (const offer of this.#allOffers) {
      for (const suppressedId of offer.suppressed_by || []) {
        if (!knownIds.has(suppressedId)) {
          console.warn(
            '[gift-engine] suppressed_by punta a un’offerta inesistente:',
            suppressedId,
            '(dichiarato da', offer.id + ') — la soppressione non avrà alcun effetto, verificare il registro'
          );
        }
      }
    }
  }

  /** Una riga regalo portata a 0 dall'utente (cestino o input) = rifiuto volontario. */
  #detectDecline(event) {
    const lines = event.lines || [];
    for (const line of lines) {
      if (line.quantity !== 0 || !line.id) continue;
      const offerId = this.#giftKeys.get(String(line.id));
      // #giftKeys si aggiorna solo a fine reconcile (riga ~284): due eventi
      // cartLinesUpdate ravvicinati sulla stessa riga regalo, prima che il primo
      // reconcile sia passato, la troverebbero entrambi ancora mappata — senza
      // questo controllo produrrebbero due gift_decline per un solo rifiuto.
      // #pendingDecline si svuota solo dopo che il rifiuto è stato persistito
      // con successo (riga ~164): un rifiuto genuinamente successivo, dopo un
      // reconcile riuscito, non viene soppresso da qui.
      if (!offerId || this.#pendingDecline.has(offerId)) continue;
      this.#pendingDecline.add(offerId);
      window.DbTrack?.push('gift_decline', { offer_id: offerId });
    }
  }

  /**
   * L'utente ripensa al rifiuto: rimuove il flag e riconcilia (il regalo torna).
   * Restituisce true/false: il chiamante (DermabellaGiftReadd, sotto) lo usa per
   * dare un segnale al cliente sul fallimento — un click che non fa nulla e non
   * lo dice è la stessa famiglia del difetto chiuso da C1 sul carrello (N4).
   */
  async readd(offerId) {
    try {
      await this.#setAttributes({ [DECLINED_PREFIX + offerId]: '' });
    } catch (e) {
      // Stessa disciplina di atc_result: non si traccia un evento per un'azione
      // che non si sa ancora essere riuscita. Il rifiuto resta persistito se il
      // POST fallisce (nessuna scrittura ottimistica), quindi non si tenta
      // nemmeno reconcile() su uno stato che il server non ha confermato.
      return false;
    }
    window.DbTrack?.push('gift_readd', { offer_id: offerId });
    await this.reconcile();
    return true;
  }

  async reconcile({ clearDeclined = false } = {}) {
    if (this.#busy) {
      // Coalescing: si accoda una sola riconciliazione, ma le OPZIONI di tutte le
      // chiamate accodate nel frattempo vanno preservate (OR logico), altrimenti
      // un clearDeclined:true richiesto durante un reconcile in corso verrebbe
      // silenziosamente perso dal rerun in `finally` (che altrimenti gira senza
      // argomenti).
      this.#pendingClearDeclined = this.#pendingClearDeclined || clearDeclined;
      this.#pending = true;
      return;
    }
    this.#busy = true;
    let mutated = false;
    const changed = [];
    try {
      let cart = await this.#fetchCart();

      // 1. Persisti i rifiuti appena rilevati / azzera i rifiuti se richiesto (nuovo ATC).
      const attrs = {};
      for (const offerId of this.#pendingDecline) attrs[DECLINED_PREFIX + offerId] = '1';
      if (clearDeclined) {
        for (const offer of this.#offers) attrs[DECLINED_PREFIX + offer.id] = '';
      }
      if (Object.keys(attrs).length > 0) {
        await this.#setAttributes(attrs);
        // Cancellare SOLO dopo la scrittura riuscita: se il POST fallisce (throw),
        // il rifiuto pendente resta e la prossima riconciliazione ritenta la
        // persistenza, invece di perdere il rifiuto e far ricomparire il regalo.
        this.#pendingDecline.clear();
        cart = await this.#fetchCart();
        mutated = true;
      }

      // 2. Riconcilia ogni offerta sullo stato reale del carrello.
      // Le quantità trigger si calcolano prima, per tutte le offerte, così la
      // sospensione (suppressed_by) può confrontarle senza dipendere
      // dall'ordine di iterazione né dalle mutazioni fatte nel frattempo.
      const offersForCart = this.#offersForCart(cart);
      const triggerQtyByOffer = new Map();
      for (const offer of offersForCart) {
        const qty = cart.items
          .filter((item) => !this.#isGiftItem(item) && offer.trigger_variant_ids.includes(item.variant_id))
          .reduce((sum, item) => sum + item.quantity, 0);
        triggerQtyByOffer.set(offer.id, qty);
      }

      for (const offer of offersForCart) {
        const triggerQty = triggerQtyByOffer.get(offer.id) || 0;
        const giftLines = cart.items.filter(
          (item) => this.#isGiftItem(item) && item.properties._offer_id === offer.id
        );
        const declined = cart.attributes[DECLINED_PREFIX + offer.id] === '1';
        const suppressedBy = offer.suppressed_by || [];
        const suppressed = suppressedBy.some((id) => (triggerQtyByOffer.get(id) || 0) > 0);

        if (triggerQty === 0 || suppressed || declined) {
          // Trigger assente, offerta soppiantata da un'altra (sconto set
          // tutto-o-niente) o rifiuto volontario: nessuna riga regalo deve
          // restare. Il rifiuto è di COPPIA: si azzerano TUTTE le righe
          // regalo dell'offerta, mai solo una.
          for (const line of giftLines) {
            await this.#changeLine(line.key, 0);
            changed.push({ id: String(line.key), quantity: 0 });
            mutated = true;
          }
          continue;
        }

        // Per ciascun regalo dell'offerta: assicura la riga, tetto quantità
        // sulla prima corrispondenza, azzeramento di eventuali duplicati.
        for (const gift of offer.gifts) {
          const matches = giftLines.filter((line) => line.variant_id === gift.variant_id);
          if (matches.length === 0) {
            // Offerta disabilitata dalla guardia regali a pagamento (§ sotto):
            // non riaggiungere, altrimenti il regalo tornerebbe a pagamento e
            // verrebbe azzerato di nuovo a ogni riconciliazione (churn).
            if (this.#disabledOffers.has(offer.id)) continue;
            await this.#addGift(offer, gift);
            window.DbTrack?.push('gift_add', { offer_id: offer.id, gift_variant_id: gift.variant_id });
            mutated = true;
            continue;
          }
          const [first, ...extra] = matches;
          if (first.quantity !== gift.qty) {
            await this.#changeLine(first.key, gift.qty);
            changed.push({ id: String(first.key), quantity: gift.qty });
            mutated = true;
          }
          for (const line of extra) {
            await this.#changeLine(line.key, 0);
            changed.push({ id: String(line.key), quantity: 0 });
            mutated = true;
          }
        }

        // Righe regalo di questa offerta rimaste orfane (variante non più
        // prevista dalla configurazione): azzerate.
        const giftVariantIds = offer.gifts.map((gift) => gift.variant_id);
        for (const line of giftLines) {
          if (!giftVariantIds.includes(line.variant_id)) {
            await this.#changeLine(line.key, 0);
            changed.push({ id: String(line.key), quantity: 0 });
            mutated = true;
          }
        }
      }

      // Righe regalo orfane di offerte non più esistenti (es. rename degli
      // id): non corrispondono a nessuna offerta corrente, vanno rimosse.
      const currentOfferIds = new Set(offersForCart.map((offer) => offer.id));
      for (const line of cart.items) {
        if (this.#isGiftItem(line) && !currentOfferIds.has(line.properties._offer_id)) {
          await this.#changeLine(line.key, 0);
          changed.push({ id: String(line.key), quantity: 0 });
          mutated = true;
        }
      }

      // 3. Aggiorna la mappa regali e, se serve, notifica i componenti del tema.
      cart = await this.#fetchCart();

      // Guardia regali a pagamento: se lo sconto server non ha azzerato una riga
      // regalo (sconto disattivato/rinominato in admin dopo la creazione della
      // config), il motore non deve MAI lasciare un regalo a pagamento nel
      // carrello. La riga viene azzerata e l'offerta disabilitata per il resto
      // della sessione (vedi #disabledOffers), altrimenti verrebbe riaggiunta al
      // giro successivo e ritrovata di nuovo a pagamento (churn add/remove).
      // Nota: se il regalo appena aggiunto in QUESTO stesso giro risulta ancora
      // a pagamento, è corretto che venga azzerato qui — è esattamente il caso
      // che questa guardia deve intercettare.
      let guardMutated = false;
      for (const item of cart.items) {
        if (!this.#isGiftItem(item) || item.final_line_price <= 0) continue;
        const offerId = item.properties._offer_id;
        const offer = offersForCart.find((o) => o.id === offerId);
        await this.#changeLine(item.key, 0);
        changed.push({ id: String(item.key), quantity: 0 });
        mutated = true;
        guardMutated = true;
        this.#disabledOffers.add(offerId);
        console.warn(
          '[gift-engine] regalo non scontato dal server, offerta disabilitata:',
          offerId,
          'atteso sconto:',
          offer ? offer.discount_name : undefined
        );
      }
      if (guardMutated) cart = await this.#fetchCart();

      this.#giftKeys = new Map(
        cart.items
          .filter((item) => this.#isGiftItem(item))
          .map((item) => [String(item.key), item.properties._offer_id])
      );
      if (mutated) {
        // Le righe aggiunte via #addGift hanno una chiave nota solo ora: le
        // recuperiamo dallo stato reale del carrello appena riletto.
        cart.items
          .filter((i) => this.#isGiftItem(i))
          .forEach((i) => {
            if (!changed.some((c) => c.id === String(i.key))) changed.push({ id: String(i.key), quantity: i.quantity });
          });
        const lines = changed.length > 0 ? changed : cart.items.map((i) => ({ id: String(i.key), quantity: i.quantity }));
        // Lo schema eventi rifiuta lines vuote e quantity 0 su 'update': le righe
        // azzerate viaggiano come 'remove' (stessa convenzione del tema). Se non
        // c'è nulla da notificare (solo attributi, carrello vuoto), niente dispatch.
        const updated = lines.filter((l) => l.quantity > 0);
        const removed = lines.filter((l) => l.quantity === 0);
        // Le due partizioni sono indipendenti: in un passaggio "misto" (es. un
        // regalo orfano azzerato e un altro regalo aggiunto nella stessa
        // riconciliazione) servono entrambi gli eventi, altrimenti la
        // rimozione non verrebbe mai annunciata ai componenti del tema.
        const partitions = [];
        if (updated.length > 0) partitions.push(['update', updated]);
        if (removed.length > 0) partitions.push(['remove', removed]);
        if (partitions.length > 0) this.#notify(cart, partitions);
      }
    } catch (error) {
      console.warn('[gift-engine] riconciliazione fallita', error);
    } finally {
      this.#busy = false;
      if (this.#pending) {
        this.#pending = false;
        const pendingClearDeclined = this.#pendingClearDeclined;
        this.#pendingClearDeclined = false;
        this.reconcile({ clearDeclined: pendingClearDeclined });
      }
    }
  }

  #isGiftItem(item) {
    return Boolean(item.properties) && item.properties._gift === '1';
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`GET /cart.js ${res.status}`);
    return res.json();
  }

  async #addGift(offer, gift) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: gift.variant_id,
            quantity: gift.qty,
            properties: { _gift: '1', _offer_id: offer.id },
          },
        ],
      }),
    });
    if (!res.ok) throw new Error(`POST /cart/add.js ${res.status}`);
  }

  async #changeLine(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error(`POST /cart/change.js ${res.status}`);
  }

  async #setAttributes(attributes) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!res.ok) throw new Error(`POST /cart/update.js ${res.status}`);
  }

  /**
   * Notifica drawer e contatore che il carrello è cambiato.
   * action 'update'/'remove' (mai 'add') per NON auto-aprire il drawer a ogni
   * riconciliazione. `partitions` è un elenco di coppie [action, lines]: in
   * un passaggio misto (righe aggiornate + righe azzerate) arrivano entrambe
   * le coppie e vengono dispacciate in sequenza, sotto lo stesso flag
   * anti-loop, per evitare che il motore reagisca ai propri eventi.
   */
  #notify(cart, partitions) {
    this.#ownDispatch = true;
    try {
      for (const [action, lines] of partitions) {
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action,
            context: 'cart',
            lines,
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: false },
            }),
          })
        );
      }
    } finally {
      this.#ownDispatch = false;
    }
  }
}

/** Bottone "Riaggiungi" renderizzato in Liquid dal carrello (vedi dermabella-offers-config, mode readd_rows). */
class DermabellaGiftReadd extends HTMLElement {
  #bound = false;

  connectedCallback() {
    // Listener delegato sull'elemento custom stesso (non sul bottone interno):
    // il morph del carrello può ripatchare/sostituire i figli, e un listener
    // legato al nodo bottone resterebbe orfano. Guardia #bound perché
    // connectedCallback può rieseguire (es. re-inserimento nel DOM dopo morph).
    if (this.#bound) return;
    this.#bound = true;
    this.addEventListener('click', async (event) => {
      const btn = event.target.closest('button');
      if (!btn) return;
      const offerId = this.dataset.offerId;
      if (!offerId) return;
      const ok = await window.DermabellaGifts?.readd(offerId);
      // false, non undefined: undefined vorrebbe dire "DermabellaGifts non è mai
      // partito" (init() uscita anticipata, es. #allOffers vuoto ovunque), un
      // caso in cui questo bottone non dovrebbe nemmeno essere nel DOM — non è
      // il fallimento di rete che N4 riguarda, non merita lo stesso segnale.
      // Nessun reconcile() è girato su un readd fallito (vedi readd() sopra),
      // quindi il carrello non è stato morphato: btn resta lo stesso nodo.
      if (ok === false) {
        const testoOriginale = btn.textContent;
        btn.textContent = 'Riprova';
        setTimeout(() => {
          if (btn.textContent === 'Riprova') btn.textContent = testoOriginale;
        }, 3000);
      }
    });
  }
}

const engine = new DermabellaGiftEngine();
window.DermabellaGifts = engine;
if (!customElements.get('dermabella-gift-readd')) {
  customElements.define('dermabella-gift-readd', DermabellaGiftReadd);
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => engine.init());
} else {
  engine.init();
}
